import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FaPlay, FaForward, FaTimes, FaPlus, FaPhone, FaUndo, FaExchangeAlt } from "react-icons/fa";
import { updateQueueStatus } from "../../../redux/slices/QueueManagementSlice";
import Button from "../../../shared/CustomButton";
import doctor from "../../../assets/images/Doctor.jpg"
export default function QueueDashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { queueList } = useSelector(state => state.queueManagement);

  // Get current serving token (first "In Progress" or first "Waiting")
  const currentToken = queueList.find(item => item.status === "In Progress") || 
                      queueList.find(item => item.status === "Waiting");
  
  // Get waiting queue (only "Waiting" status)
  const waitingQueue = queueList.filter(item => item.status === "Waiting").slice(0, 6);
  
  // Timer state for serving time
  const [servingTime, setServingTime] = useState({ hours: 0, minutes: 45, seconds: 13 });
  const [isTimerRunning, setIsTimerRunning] = useState(true);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setServingTime(prev => {
          let newSeconds = prev.seconds + 1;
          let newMinutes = prev.minutes;
          let newHours = prev.hours;

          if (newSeconds >= 60) {
            newSeconds = 0;
            newMinutes += 1;
          }
          if (newMinutes >= 60) {
            newMinutes = 0;
            newHours += 1;
          }

          return {
            hours: newHours,
            minutes: newMinutes,
            seconds: newSeconds
          };
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  // Handle queue actions
  const handleNext = () => {
    if (currentToken) {
      // Mark current as completed
      dispatch(updateQueueStatus({ id: currentToken.id, status: "Completed" }));
    }
  };

  const handleCall = () => {
    if (currentToken && currentToken.status === "Waiting") {
      dispatch(updateQueueStatus({ id: currentToken.id, status: "In Progress" }));
      setIsTimerRunning(true);
    }
  };

  const handleRecall = () => {
    // Logic to recall current token
    console.log("Recalling token:", currentToken?.queueNumber);
  };

  const handleTransfer = () => {
    // Logic to transfer current token
    console.log("Transferring token:", currentToken?.queueNumber);
  };

  const handleStart = () => {
    setIsTimerRunning(true);
  };

  const handleClose = () => {
    if (currentToken) {
      dispatch(updateQueueStatus({ id: currentToken.id, status: "Cancelled" }));
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-100 overflow-y-auto">
      {/* Doctor Header Section */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Doctor Profile Picture */}
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <img
                src={doctor}
                alt="Doctor"
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-12 h-12 bg-blue-500 rounded-full items-center justify-center text-black font-bold text-lg">
                DR
              </div>
            </div>
            
            {/* Doctor Information */}
            <div>
              <h2 className="text-xl font-bold text-gray-800">Dr. Ahmed Hassan</h2>
              <p className="text-gray-600">General Practitioner</p>
              <p className="text-sm text-gray-500">Room 203 - Consultation</p>
            </div>
          </div>

          {/* Date and Time */}
          <div className="text-right">
            <div className="text-lg font-semibold text-gray-800">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {new Date().toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Panel - Current Serving */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-lg p-6">
            <div className="text-center mb-8">
              <h2 className="text-lg font-medium text-gray-600 mb-2">
                {t("Current Serving")}
              </h2>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {t("Token Number")}
              </h1>
              
              {/* Token Display */}
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-8 mb-6 max-w-sm mx-auto">
                <div className="text-6xl font-bold text-orange-500">
                  {currentToken?.queueNumber || "---"}
                </div>
              </div>

              {/* Serving Time */}
              <div className="mb-2">
                <span className="text-sm text-gray-600">{t("Serving Time")}</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">
                {String(servingTime.hours).padStart(2, '0')}:
                {String(servingTime.minutes).padStart(2, '0')}:
                {String(servingTime.seconds).padStart(2, '0')}
              </div>

              {/* Statistics */}
              <div className="flex justify-center gap-8 text-sm text-gray-600">
                <div>
                  <div className="font-medium">
                    {queueList.filter(item => item.status === "Completed").length}
                  </div>
                  <div>{t("Total Served Tokens")}</div>
                </div>
                <div>
                  <div className="font-medium">{t("Excellent")}</div>
                  <div>{t("Performance Status")}</div>
                </div>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleNext}
                disabled={!currentToken}
                label={t("Next")}
                icon={<FaForward className="text-xl" />}
                className="py-4 px-6 rounded-lg transition-colors duration-200"
              />

              <Button
                onClick={handleCall}
                disabled={!currentToken || currentToken.status === "In Progress"}
                label={t("Call")}
                icon={<FaPhone className="text-xl" />}
                className="py-4 px-6 rounded-lg transition-colors duration-200"
              />

              <Button
                onClick={handleRecall}
                disabled={!currentToken}
                label={t("Recall")}
                icon={<FaUndo className="text-xl" />}
                className="py-4 px-6 rounded-lg transition-colors duration-200"
              />

              <Button
                onClick={handleTransfer}
                disabled={!currentToken}
                label={t("Transfer")}
                icon={<FaExchangeAlt className="text-xl" />}
                className="py-4 px-6 rounded-lg transition-colors duration-200"
              />

              <Button
                onClick={handleStart}
                label={t("Start")}
                icon={<FaPlay className="text-xl" />}
                className="py-4 px-6 rounded-lg transition-colors duration-200"
              />

              <Button
                onClick={handleClose}
                disabled={!currentToken}
                label={t("Close")}
                icon={<FaTimes className="text-xl" />}
                className="py-4 px-6 rounded-lg transition-colors duration-200"
              />
            </div>
          </div>

          {/* Right Panel - Queue List */}
          <div className="bg-blue-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-black">{t("Patients")}</h3>
             
            </div>

            {/* Queue Items */}
            <div className="space-y-3 max-h-80 !overflow-y-auto scrollbar">
              {waitingQueue.length > 0 ? (
                waitingQueue.map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-blue-500 rounded-lg p-3 flex justify-between items-center hover:bg-blue-400 transition-colors duration-200"
                  >
                    <div>
                      <div className="font-bold text-lg text-black">{item.queueNumber}</div>
                      <div className="text-sm text-black">{item.patientName}</div>
                      <div className="text-xs text-gray-500">{item.department}</div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-black">{item.appointmentTime}</div>
                      <div className="text-xs mt-1">
                        {item.priority === "Emergency" && (
                          <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs">
                            {t("Emergency")}
                          </span>
                        )}
                        {item.priority === "High" && (
                          <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs">
                            {t("High")}
                          </span>
                        )}
                        {item.priority === "Medium" && (
                          <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs">
                            {t("Medium")}
                          </span>
                        )}
                        {item.priority === "Low" && (
                          <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                            {t("Low")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-black py-8">
                  <div className="text-lg mb-2">{t("No patients in queue")}</div>
                  <div className="text-sm text-gray-500">{t("Queue is empty")}</div>
                </div>
              )}
            </div>

            {/* Queue Statistics */}
            <div className="mt-6 pt-4 border-t border-blue-500">
              <div className="flex justify-between text-sm text-black">
                <span>{t("Total in Queue")}:</span>
                <span className="font-bold">{waitingQueue.length}</span>
              </div>
              <div className="flex justify-between text-sm mt-1 text-black">
                <span>{t("Average Wait")}:</span>
                <span className="font-bold">15 {t("min")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats Panel */}
        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {queueList.filter(item => item.status === "Waiting").length}
              </div>
              <div className="text-gray-600 font-medium">{t("Waiting")}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {queueList.filter(item => item.status === "Completed").length}
              </div>
              <div className="text-gray-600 font-medium">{t("Completed")}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">
                {queueList.filter(item => item.status === "In Progress").length}
              </div>
              <div className="text-gray-600 font-medium">{t("In Progress")}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {queueList.filter(item => item.status === "Cancelled").length}
              </div>
              <div className="text-gray-600 font-medium">{t("Cancelled")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}